import { ReportFilterBar } from "@/components/features/reports/ReportFilterBar";
import { ReportsPageLayout } from "@/components/features/reports/ReportsPageLayout";
import {
  getRoleAssignmentsReportColumns,
  getRoleAssignmentStatusColumnId,
} from "@/components/features/reports/RoleAssignmentsReportCard/role-assignments-report-columns";
import {
  Card,
  CardContent,
  DataTable,
  GlobalNotAuthorized,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import {
  retrieveReportsRoleAssignmentsQueryOptions,
  retrieveRoleAssignmentReportRolesQueryOptions,
} from "@/lib/data/react-query/reports/reports-role-assignments";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import type { ReportsRoleAssignmentRow } from "@/lib/types";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { BriefcaseBusiness, Users } from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";

export const Route = createFileRoute("/_auth/reports/role-assignments")({
  component: RouteComponent,
  validateSearch: z.object({ role: z.string().optional() }),
});

function RouteComponent() {
  const { hasPagePermission } = useUser();
  const navigate = Route.useNavigate();
  const { role: roleId } = Route.useSearch();
  const canViewReport = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersReportsRoleAssignments,
  );

  const {
    data: roleOptions,
    isLoading: isSettingsLoading,
    isError: isSettingsError,
  } = useQuery({
    ...retrieveRoleAssignmentReportRolesQueryOptions(),
    enabled: canViewReport,
  });
  const {
    data: report,
    isLoading: isReportLoading,
    isError: isReportError,
  } = useQuery({
    ...retrieveReportsRoleAssignmentsQueryOptions(roleId ?? ""),
    enabled: canViewReport && Boolean(roleId),
  });

  const roles = roleOptions?.data ?? [];
  const statuses = useMemo(
    () => report?.data.statuses ?? [],
    [report?.data.statuses],
  );
  const rows = report?.data.rows ?? [];
  const columns = useMemo(
    () => getRoleAssignmentsReportColumns(statuses),
    [statuses],
  );
  const tableRenderKey = columns
    .map((column) => column.id)
    .filter(Boolean)
    .join("|");
  const selectedRole = roles.find((role) => role.id === roleId);

  const getCsvValue = (row: ReportsRoleAssignmentRow, columnId: string) => {
    const status = statuses.find(
      (item) => getRoleAssignmentStatusColumnId(item, statuses) === columnId,
    );
    if (status) {
      return (row.statusCounts[status.id] ?? 0).toLocaleString();
    }
    switch (columnId) {
      case "User":
        return row.userName ?? "Restricted user";
      case "Total":
        return row.totalMatters.toLocaleString();
      case "In last 24 hours":
        return row.assignedLast24Hours.toLocaleString();
      case "In last 7 days":
        return row.assignedLast7Days.toLocaleString();
      case "In last 30 days":
        return row.assignedLast30Days.toLocaleString();
      default:
        return "";
    }
  };

  if (!canViewReport) {
    return <GlobalNotAuthorized />;
  }

  return (
    <ReportsPageLayout title="Role assignments report">
      <ReportFilterBar
        appliedFilters={
          selectedRole
            ? [
                {
                  key: `role-${selectedRole.id}`,
                  label: `Role: ${selectedRole.label}`,
                  onRemove: () => {
                    void navigate({ search: {}, replace: true });
                  },
                },
              ]
            : []
        }
        onClearFilters={async () => {
          await navigate({ search: {}, replace: true });
        }}
      >
        <Select
          value={roleId ?? ""}
          onValueChange={async (role) => {
            await navigate({ search: { role }, replace: true });
          }}
        >
          <SelectTrigger className="h-11 w-[280px]" aria-label="Select role">
            <SelectValue placeholder="Select role" />
          </SelectTrigger>
          <SelectContent>
            {roles.map((role) => (
              <SelectItem key={role.id} value={role.id}>
                {role.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ReportFilterBar>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="border-border/60 shadow-sm">
          <CardContent className="space-y-2 pt-4">
            <div className="flex items-center gap-3">
              <div className="bg-muted flex h-10 w-10 items-center justify-center rounded-full">
                <BriefcaseBusiness className="h-5 w-5" />
              </div>
              <Text variant="subHeading">Total matter assignments</Text>
            </div>
            <Text variant="title" className="text-2xl leading-8">
              {(report?.data.metrics.totalAssignments ?? 0).toLocaleString()}
            </Text>
          </CardContent>
        </Card>
        <Card className="border-border/60 shadow-sm">
          <CardContent className="space-y-2 pt-4">
            <div className="flex items-center gap-3">
              <div className="bg-muted flex h-10 w-10 items-center justify-center rounded-full">
                <Users className="h-5 w-5" />
              </div>
              <Text variant="subHeading">Firm users</Text>
            </div>
            <Text variant="title" className="text-2xl leading-8">
              {(report?.data.metrics.totalUsers ?? 0).toLocaleString()}
            </Text>
          </CardContent>
        </Card>
      </div>

      <DataTable
        entityLabelPlural="users"
        key={tableRenderKey}
        allowLocalStorage={false}
        hideSearchBar
        columns={columns}
        data={rows}
        onRowClick={() => {}}
        tableName={tanstackTableNames.reportsRoleAssignments}
        isLoading={isSettingsLoading || (Boolean(roleId) && isReportLoading)}
        isError={isSettingsError || isReportError}
        noResultsMessage={
          roleId
            ? "No users found for this role assignment report."
            : "Please select a role to view assignment statistics."
        }
        leftToolbarContent={
          <Text variant="body" className="text-lg font-semibold">
            {selectedRole ? `${selectedRole.label} Assignments` : "Assignments"}
          </Text>
        }
        exportCsv={{
          fileName: "role-assignments-report.csv",
          getCellValue: getCsvValue,
        }}
      />
    </ReportsPageLayout>
  );
}
